


import React, { useState, useEffect } from 'react';
import './checkout.css';
import { useSelector } from 'react-redux';
import { getWithTokenAPI, postWithTokenAPI, deleteAPI } from '../../util/Api';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom'; // No need for useLocation if not used
import { RiPencilLine } from "react-icons/ri";
import { FaRegTrashCan } from "react-icons/fa6";
// Removed useParams as it's not used in the provided code

const BillingInfo = () => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        companyName: '',
        email: '',
        country: '',
        state: '',
        zipCode: '',
        notes: '',
        paymentMethod: 'm-pesa',
        shipToDifferentAddress: false
    });

    const [addresses, setAddresses] = useState([]);
    const [selectedAddress, setSelectedAddress] = useState('');
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [cartData, setCartData] = useState(null);
    const accessToken = useSelector(state => state.auth?.auth?.access_token);
    const userId = useSelector(state => state.auth?.auth?._id);
    const userPhoneNumber = useSelector(state => state.auth?.auth?.phone_number);
    const cartItems = useSelector((state) => state.cart.cartItems || []);
    const navigate = useNavigate();

    // New state variables for payment processing
    const [isPlacingOrder, setIsPlacingOrder] = useState(false);
    const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
    const [orderId, setOrderId] = useState(null); // To store the order ID for status checks
    const [pollingAttempts, setPollingAttempts] = useState(0);
    const MAX_POLLING_ATTEMPTS = 12; // 12 attempts * 5 seconds = 60 seconds (1 minute)

    const [addressData, setAddressData] = useState({
        name: '',
        street: '',
        city: '',
        state: '',
        zip_code: '',
        country: '',
    });

    const [isGettingLocation, setIsGettingLocation] = useState(false);
    const [locationError, setLocationError] = useState(null);

    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [addressToDelete, setAddressToDelete] = useState(null);

    const subtotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const shipping = 0;
    const total = subtotal + shipping;

    // Fetch cart data
    useEffect(() => {
        const fetchCart = async () => {
            if (!userId) return;

            try {
                const response = await getWithTokenAPI(accessToken, `/cart/find-all?user_id=${userId}`);
                if (response.statusCode === 200 && response.data.Cart.length > 0) {
                    setCartData(response.data.Cart[0]);
                }
            } catch (error) {
                console.error("Error fetching cart:", error);
            }
        };

        fetchCart();
    }, [userId, accessToken]);

    // Fetch addresses
    useEffect(() => {
        const fetchAddresses = async () => {
            if (!userId) return;

            try {
                setLoading(true);
                const response = await getWithTokenAPI(accessToken, `/address/find-all?user_id=${userId}`);

                if (response.statusCode === 200) {
                    const fetchedAddresses = response.data.address || [];
                    setAddresses(fetchedAddresses);
                    if (fetchedAddresses.length > 0) {
                        setSelectedAddress(fetchedAddresses[0]._id);
                    }
                }
            } catch (error) {
                console.error("Error fetching addresses:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchAddresses();
    }, [userId, accessToken]);

    const handleAddressChange = (addressId) => {
        setSelectedAddress(addressId);
        if (isEditing) {
            setIsEditing(false);
            setShowForm(false);
        }
    };

    const handleAddNewAddress = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setShowForm(!showForm);
        if (!showForm) {
            setIsEditing(false);
            setAddressData({
                name: '',
                street: '',
                city: '',
                state: '',
                zip_code: '',
                country: '',
            });
        }
    };

    const handleAddressInputChange = (e) => {
        const { id, value } = e.target;
        setAddressData(prev => ({
            ...prev,
            [id]: value
        }));
    };

    const getCurrentLocation = () => {
        return new Promise((resolve, reject) => {
            if (!navigator.geolocation) {
                reject(new Error('Geolocation is not supported by your browser'));
                return;
            }

            setIsGettingLocation(true);
            setLocationError(null);

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setIsGettingLocation(false);
                    resolve({
                        lat: position.coords.latitude,
                        long: position.coords.longitude
                    });
                },
                (error) => {
                    setIsGettingLocation(false);
                    reject(error);
                }
            );
        });
    };

    const handleSaveAddress = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        try {
            if (!addressData.name || !addressData.street || !addressData.city ||
                !addressData.state || !addressData.zip_code || !addressData.country) {
                toast.error("Please fill in all address fields", {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    toastId: `address-validation-${Date.now()}`
                });
                return;
            }

            if (!accessToken) {
                toast.error("Please login to save address", {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    toastId: `auth-error-${Date.now()}`
                });
                return;
            }

            if (!userId) {
                toast.error("User ID not found", {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    toastId: `user-error-${Date.now()}`
                });
                return;
            }

            const addressPayload = {
                name: addressData.name,
                street: addressData.street,
                city: addressData.city,
                state: addressData.state,
                zip_code: addressData.zip_code,
                country: addressData.country,
                user_id: userId
            };

            try {
                setIsGettingLocation(true);
                setLocationError(null);
                const location = await getCurrentLocation();
                addressPayload.lat = location.lat.toString();
                addressPayload.long = location.long.toString();
            } catch (error) {
                // Location not available, proceeding without coordinates
                setLocationError(error.message); // Display a message if location access is denied or unavailable
            }

            const response = await postWithTokenAPI(accessToken, '/address/create', addressPayload);

            if (response?.statusCode === 200 || response?.status === 200) {
                toast.success("Address saved successfully!", {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    toastId: `address-success-${Date.now()}`
                });
                setShowForm(false);
                setAddressData({
                    name: '',
                    street: '',
                    city: '',
                    state: '',
                    zip_code: '',
                    country: '',
                });

                const updatedAddresses = await getWithTokenAPI(accessToken, `/address/find-all?user_id=${userId}`);
                if (updatedAddresses?.statusCode === 200) {
                    setAddresses(updatedAddresses.data.address || []);
                }
            }
        } catch (error) {
            console.error('Error saving address:', error);
            toast.error("Failed to save address", {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                toastId: `address-error-${Date.now()}`
            });
        } finally {
            setIsGettingLocation(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

const handleSubmit = async (e) => {
    e.preventDefault();

    if (isPlacingOrder || isPaymentProcessing) return;

    if (!selectedAddress) {
        toast.error("Please select a delivery address");
        return;
    }

    if (!cartData) {
        toast.error("Cart is empty");
        return;
    }

    // New state to indicate that we are starting the payment process
    setIsPaymentProcessing(true);
    toast.info("Initiating payment...", { autoClose: false, toastId: 'initiatingPayment' });

    // Directly call initiatePayment from handleSubmit
    await initiatePayment();
};

const initiatePayment = async () => {
    const phoneWithoutPlus = userPhoneNumber?.replace(/^\+/, '');

    const paymentPayload = {
        cart_id: cartData._id,
        phoneNumber: phoneWithoutPlus,
    };

    let pollingIntervalId;
    let timeoutId;

    try {
        // Step 1: Initiate payment API call
        const response = await postWithTokenAPI(accessToken, '/order/intiatepayment', paymentPayload);

        if (response?.statusCode === 200 && response?.data) {
            toast.dismiss('initiatingPayment');
            toast.success("Payment initiated. Please complete the payment on your device.", { autoClose: 5000 });

            const POLLING_INTERVAL = 15000;
            const TOTAL_POLLING_DURATION = 90000;
            let currentPollingAttempt = 0;

            // Step 2: Start polling for payment status
            pollingIntervalId = setInterval(async () => {
                currentPollingAttempt++;
                console.log(`Checking payment status (Attempt ${currentPollingAttempt})...`);

                try {
                    const statusRes = await getWithTokenAPI(accessToken, `/order/checkstatus?cart_id=${cartData._id}`);

                    
                    const isPaymentSuccess = (statusRes?.data?.mpesa_status?.ResponseCode === "0" && !(statusRes?.message === "Payment status retrieved")) 
                                            

                    if (isPaymentSuccess) {
                        clearInterval(pollingIntervalId);
                        clearTimeout(timeoutId);
                        toast.dismiss('initiatingPayment');
                        toast.success('Payment successful!', { autoClose: 3000 });

                        // *** Step 3: Create the order after a successful payment ***
                        setIsPlacingOrder(true);
                        toast.info("Payment confirmed, placing your order...", { autoClose: false, toastId: 'placingOrder' });
                        
                        const orderPayload = {
                            address_id: selectedAddress,
                            cart_id: cartData._id,
                            payment_method: formData.paymentMethod,
                            shipping: "free"
                        };

                        const orderResponse = await postWithTokenAPI(accessToken, '/order/create', orderPayload);

                        if (orderResponse.statusCode === 200) {
                            toast.dismiss('placingOrder');
                            toast.success("Order placed successfully!", { autoClose: 3000 });
                            navigate('/order_view');
                        } else {
                            toast.dismiss('placingOrder');
                            toast.error("Order creation failed after payment. Please contact support.");
                        }

                        setIsPlacingOrder(false);
                        setIsPaymentProcessing(false);

                    } else if (currentPollingAttempt >= Math.ceil(TOTAL_POLLING_DURATION / POLLING_INTERVAL)) {
                        clearInterval(pollingIntervalId);
                        clearTimeout(timeoutId);
                        toast.dismiss('initiatingPayment');
                        toast.error('Payment timed out. Please try again.', { autoClose: 5000 });
                        setIsPaymentProcessing(false);
                    }
                    // *** MODIFIED LOGIC END ***

                } catch (error) {
                    console.error('Error checking payment status:', error);
                    if (currentPollingAttempt >= Math.ceil(TOTAL_POLLING_DURATION / POLLING_INTERVAL)) {
                        clearInterval(pollingIntervalId);
                        clearTimeout(timeoutId);
                        toast.dismiss('initiatingPayment');
                        toast.error('Payment status check failed. Please check your order status.', { autoClose: 5000 });
                        setIsPaymentProcessing(false);
                    }
                }
            }, POLLING_INTERVAL);

            timeoutId = setTimeout(() => {
                clearInterval(pollingIntervalId);
                if (isPaymentProcessing) {
                    toast.dismiss('initiatingPayment');
                    toast.error('Payment processing exceeded the time limit. Please check your order status.', { autoClose: 5000 });
                    setIsPaymentProcessing(false);
                }
            }, TOTAL_POLLING_DURATION);
        } else {
            // Initial payment initiation failed
            toast.dismiss('initiatingPayment');
            toast.error('Payment initiation failed. Please try again.', { autoClose: 3000 });
            setIsPaymentProcessing(false);
        }
    } catch (error) {
        console.error("Error initiating payment:", error);
        toast.dismiss('initiatingPayment');
        toast.error('Failed to initiate payment. Please check your network and try again.', { autoClose: 5000 });
        setIsPaymentProcessing(false);
        clearInterval(pollingIntervalId);
        clearTimeout(timeoutId);
    }
};



    const handleDeleteClick = (addressId, e) => {
        e.stopPropagation();
        setAddressToDelete(addressId);
        setShowDeleteConfirm(true);
    };

    const handleDeleteConfirm = async () => {
        if (!addressToDelete) return;

        try {
            const payload = {
                _id: addressToDelete
            };

            const response = await deleteAPI(accessToken, '/address/delete', payload);

            if (response?.statusCode === 200) {
                toast.success("Address deleted successfully", {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    toastId: `delete-success-${Date.now()}`
                });
                setAddresses(prevAddresses => prevAddresses.filter(addr => addr._id !== addressToDelete));
                if (selectedAddress === addressToDelete) {
                    setSelectedAddress('');
                }
            }
        } catch (error) {
            console.error('Error deleting address:', error);
            toast.error("Failed to delete address", {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                toastId: `delete-error-${Date.now()}`
            });
            if (error?.response?.status === 401) {
                navigate('/login');
            }
        } finally {
            setShowDeleteConfirm(false);
            setAddressToDelete(null);
        }
    };

    const handleDeleteCancel = () => {
        setShowDeleteConfirm(false);
        setAddressToDelete(null);
    };

    return (
        <>
            {showDeleteConfirm && (
                <div className="delete-confirmation-overlay">
                    <div className="delete-confirmation-popup">
                        <h3>Delete Address</h3>
                        <p>Are you sure you want to delete this address?</p>
                        <div className="delete-confirmation-buttons">
                            <button
                                className="delete-confirm-btn"
                                onClick={handleDeleteConfirm}
                            >
                                Delete
                            </button>
                            <button
                                className="delete-cancel-btn"
                                onClick={handleDeleteCancel}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="billing-container">
                <h1 className="billing-title">Billing Information</h1>

                <div className="checkout-grid">
                    <form onSubmit={handleSubmit} className="billing-form">
                        <div className="merged-form-section">
                            <div className="address-selection-form">
                                <h2 className="section-title">Select Delivery Address</h2>
                                {loading ? (
                                    <div className="loading-message">Loading addresses...</div>
                                ) : addresses.length === 0 ? (
                                    <div className="no-addresses-message">No saved addresses found</div>
                                ) : (
                                    <div className="address-list">
                                        {addresses.map((address) => (
                                            <div
                                                key={address._id}
                                                className={`address-item ${selectedAddress === address._id ? 'selected' : ''}`}
                                                onClick={() => handleAddressChange(address._id)}
                                            >
                                                <input
                                                    type="radio"
                                                    name="address"
                                                    value={address._id}
                                                    checked={selectedAddress === address._id}
                                                    onChange={() => handleAddressChange(address._id)}
                                                    className="address-radio"
                                                />
                                                <div className="address-details">
                                                    <div className="address-name">{address.name}</div>
                                                    <div className="address-street">{address.street}</div>
                                                    <div className="address-city-state">
                                                        {address.city}, {address.state} {address.zip_code}
                                                    </div>
                                                    <div className="address-country">{address.country}</div>
                                                </div>
                                                <button
                                                    type="button"
                                                    className="edit-address-btn"
                                                    onClick={() => navigate('/address')}
                                                >
                                                    <RiPencilLine className='pencil' />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="delete-address-btn"
                                                    onClick={(e) => handleDeleteClick(address._id, e)}
                                                >
                                                    <FaRegTrashCan className='delete-icon' />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <button
                                type="button"
                                className="toggle-form-btn"
                                onClick={handleAddNewAddress}
                            >
                                {showForm ? 'Cancel' : 'Add New Address'}
                            </button>

                            {showForm && (
                                <div
                                    className="billing-address-form"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div className="input-block">
                                        <label htmlFor="name">Name</label>
                                        <input
                                            type="text"
                                            id="name"
                                            value={addressData.name}
                                            onChange={handleAddressInputChange}
                                            required
                                        />
                                    </div>

                                    <div className="input-block full-width">
                                        <label htmlFor="street">Street Address</label>
                                        <input
                                            type="text"
                                            id="street"
                                            value={addressData.street}
                                            onChange={handleAddressInputChange}
                                            required
                                        />
                                    </div>

                                    <div className="input-block">
                                        <label htmlFor="city">City</label>
                                        <input
                                            type="text"
                                            id="city"
                                            value={addressData.city}
                                            onChange={handleAddressInputChange}
                                            required
                                        />
                                    </div>

                                    <div className="input-block">
                                        <label htmlFor="state">State</label>
                                        <input
                                            type="text"
                                            id="state"
                                            value={addressData.state}
                                            onChange={handleAddressInputChange}
                                            required
                                        />
                                    </div>

                                    <div className="input-block">
                                        <label htmlFor="zip_code">Zip Code</label>
                                        <input
                                            type="text"
                                            id="zip_code"
                                            maxLength="6" pattern="^\d{6}$"
                                            value={addressData.zip_code}
                                            onChange={handleAddressInputChange}
                                            required
                                        />
                                    </div>

                                    <div className="input-block">
                                        <label htmlFor="country">Country</label>
                                        <input
                                            type="text"
                                            id="country"
                                            value={addressData.country}
                                            onChange={handleAddressInputChange}
                                            required
                                        />
                                    </div>

                                    <div className="location-status">
                                        {isGettingLocation && (
                                            <div className="location-loading">
                                                Getting your location...
                                            </div>
                                        )}
                                        {locationError && (
                                            <div className="location-error">
                                                {locationError}
                                            </div>
                                        )}
                                    </div>

                                    <button
                                        type="button"
                                        className="billing-save-button-checkout"
                                        onClick={handleSaveAddress}
                                        disabled={isGettingLocation}
                                    >
                                        {isGettingLocation ? 'Getting Location...' : 'Save Address'}
                                    </button>
                                </div>
                            )}
                        </div>
                    </form>

                    <div className="order-sidebar">
                        <div className="order-summary">
                            <div className="order-summary-title">Order Summary</div>

                            <div className="product-list">
                                {cartItems.map((item, index) => (
                                    <div key={index} className="product">
                                        <div className="product-info-boxs">
                                            <div
                                                className="product-image"
                                                style={{ backgroundImage: `url(${item.image})` }}
                                            ></div>
                                            <div className="product-name">{item.name}</div>
                                            <div className="product-quantity">x{item.quantity}</div>
                                        </div>
                                        <div className="product-price">KSh {(item.price * item.quantity).toFixed(2)}</div>
                                    </div>
                                ))}
                            </div>

                            <div className="total-section">
                                <div className="total-row subtotals">
                                    <div className="total-label">Subtotal:</div>
                                    <div className="total-value">KSh {subtotal.toFixed(2)}</div>
                                </div>
                                <div className="cross"></div>
                                <div className="total-row shipping">
                                    <div className="total-label">VAT(16%):</div>
                                    <div className="total-value">KSh {cartData?.tax}</div>
                                </div>
                                <div className="cross"></div>
                                <div className="total-row total">
                                    <div className="total-label">Total:</div>
                                    <div className="total-value final">
                                        KSh {(total + (cartData?.tax || 0)).toFixed(2)}
                                    </div>
                                </div>
                            </div>

                            <button
                                className="place-order-button"
                                onClick={handleSubmit}
                                disabled={!cartData || isPlacingOrder || isPaymentProcessing}
                            >
                                {isPlacingOrder ? (
                                    <>
                                        <span className="spinner"></span> Placing Order...
                                    </>
                                ) : isPaymentProcessing ? (
                                    <>
                                        <span className="spinner"></span> Payment Initiating...
                                    </>
                                ) : (
                                    "Place Order"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default BillingInfo;
